import { welfordAdd } from '/bb/aggregators/welford.js';
import { FILE_SELECTION_LATENCY_MAX_MS } from '/bb/constants.js';
import { resolveField } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateField, getOrCreateForm, markFieldChange } from '/bb/helpers.js';

function resolveFileContext(target) {
  const field = resolveField(target);
  if (!field || field.type !== 'file') return null;

  const pageKey = ensureCurrentPage();
  return {
    field,
    fld: getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories),
    form: getOrCreateForm(pageKey, field.formId),
  };
}

function clickHandler(e) {
  const context = resolveFileContext(e.target);
  if (!context) return;

  context.fld.multiple_enabled = !!e.target.multiple;
  context.fld.runtime.filePickerOpenedAt = Date.now();
  context.fld.runtime.fileInputSeenSinceChange = false;
}

function inputHandler(e) {
  const context = resolveFileContext(e.target);
  if (!context) return;

  recordFileSelection(context, e.target);
  context.fld.runtime.fileInputSeenSinceChange = true;
  // Native file updates dispatch input and change in the same task. Clear the marker afterward so a later standalone
  // change is still counted as a separate integration event.
  setTimeout(() => {
    context.fld.runtime.fileInputSeenSinceChange = false;
  }, 0);
}

function changeHandler(e) {
  const context = resolveFileContext(e.target);
  if (!context) return;

  if (!context.fld.runtime.fileInputSeenSinceChange) {
    recordFileSelection(context, e.target);
  } else {
    markFieldChange(context.form, context.field.fieldName);
    context.fld.multiple_enabled = !!e.target.multiple;
  }
  context.fld.runtime.fileInputSeenSinceChange = false;
}

function cancelHandler(e) {
  const context = resolveFileContext(e.target);
  if (!context) return;

  context.fld.cancel_count += 1;
  context.fld.multiple_enabled = !!e.target.multiple;
  context.fld.runtime.filePickerOpenedAt = 0;
  context.fld.runtime.fileInputSeenSinceChange = false;
}

function recordFileSelection(context, target) {
  const { field, fld, form } = context;
  const filesCount = target.files && Number.isFinite(target.files.length) ? Math.max(0, target.files.length) : 0;

  markFieldChange(form, field.fieldName);
  fld.changes_count += 1;
  fld.files_count_last = filesCount;
  fld.files_count_total += filesCount;
  fld.files_count_max = Math.max(fld.files_count_max, filesCount);
  fld.multiple_enabled = !!target.multiple;
  if (filesCount > 1) fld.multiple_selection_count += 1;

  const openedAt = fld.runtime.filePickerOpenedAt;
  if (openedAt > 0) {
    const latency = Date.now() - openedAt;
    if (latency >= 0 && latency <= FILE_SELECTION_LATENCY_MAX_MS) {
      welfordAdd(fld._selection_latency, latency);
    }
  }
  fld.runtime.filePickerOpenedAt = 0;
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  document.addEventListener('click', clickHandler, { passive: true, capture: true });
  document.addEventListener('input', inputHandler, { passive: true, capture: true });
  document.addEventListener('change', changeHandler, { passive: true, capture: true });
  document.addEventListener('cancel', cancelHandler, { passive: true, capture: true });
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('click', clickHandler, true);
  document.removeEventListener('input', inputHandler, true);
  document.removeEventListener('change', changeHandler, true);
  document.removeEventListener('cancel', cancelHandler, true);
  attached = false;
}
